import type {
  CrmLeadFieldIntegrationBinding,
  Field,
} from '@/api/types/crm/crm.types'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { parseCrmChatProviderKey } from '@pages/Admin/CRM/components/integrations/common/lib/crmChatProviderKey'
import {
  getLeadProviderChatMeta,
  getLeadProviderChatTabs,
} from '@pages/Admin/CRM/utils/leadFields'
import { getLeadProviderChatAccounts } from '@pages/Admin/CRM/utils/leadProviderChatAccounts'
import { getWazzupChannelTypeLabel } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'

const MANUAL_INPUT_UNSUPPORTED_HINT =
  'Ручной ввод ссылки не поддерживается — чат создаётся после входящего сообщения'

/** Telegram / Wazzup TG: чат нельзя создать по username — только после входящего. */
export const TELEGRAM_CHAT_REQUIRES_INCOMING_HINT =
  'Чат создастся только после того, как клиент напишет сообщение боту. Создать чат по username нельзя.'

const TELEGRAM_FIELD_HINT = `Формат: https://t.me/username. ${TELEGRAM_CHAT_REQUIRES_INCOMING_HINT}`

const PROVIDER_HINTS: Record<string, string> = {
  vkontakte: 'Формат: https://vk.com/id123 или username профиля',
  telegram: TELEGRAM_FIELD_HINT,
  max: 'Формат: https://max.ru/u/уникальный-код',
}

const PROVIDER_PLACEHOLDERS: Record<string, string> = {
  vkontakte: 'https://vk.com/id123 или username',
  telegram: 'https://t.me/username',
  max: 'https://max.ru/u/уникальный-код',
}

const WAZZUP_HINTS: Record<string, string> = {
  telegram: TELEGRAM_FIELD_HINT,
  tgapi: 'Формат: https://t.me/username',
  whatsapp: 'Формат: https://wa.me/79001234567',
  wapi: 'Формат: https://wa.me/79001234567',
  waba: 'Формат: https://wa.me/79001234567',
  vk: 'Формат: https://vk.com/id123 (только числовой id)',
  instagram: 'Формат: https://instagram.com/username',
  viber: MANUAL_INPUT_UNSUPPORTED_HINT,
  max: MANUAL_INPUT_UNSUPPORTED_HINT,
  maxbot: MANUAL_INPUT_UNSUPPORTED_HINT,
  maxgroup: MANUAL_INPUT_UNSUPPORTED_HINT,
  avito: MANUAL_INPUT_UNSUPPORTED_HINT,
  cian: MANUAL_INPUT_UNSUPPORTED_HINT,
}

const WAZZUP_PLACEHOLDERS: Record<string, string> = {
  telegram: 'https://t.me/username',
  tgapi: 'https://t.me/username',
  whatsapp: 'https://wa.me/79001234567',
  wapi: 'https://wa.me/79001234567',
  waba: 'https://wa.me/79001234567',
  vk: 'https://vk.com/id123',
  instagram: 'https://instagram.com/username',
}

export const getCrmLeadFieldIntegrationBindings = (
  field: Field | null | undefined,
): CrmLeadFieldIntegrationBinding[] => {
  const raw = field?.integration_bindings
  return Array.isArray(raw) ? raw : []
}

/** Источник истины: binding, не unique_name. */
export const isCrmLeadIntegrationField = (
  field: Field | null | undefined,
): boolean => getCrmLeadFieldIntegrationBindings(field).length > 0

/** Auto-поле чат-интеграции (бэк: settings.managed_by_integration). */
export const isCrmLeadFieldManagedByIntegration = (
  field: Field | null | undefined,
): boolean => {
  const settings = field?.settings
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return false
  }
  return (settings as { managed_by_integration?: unknown })
    .managed_by_integration === true
}

export const filterCrmLeadIntegrationFields = (fields: Field[]): Field[] =>
  fields
    .filter((field) => field.is_active && isCrmLeadIntegrationField(field))
    .slice()
    .sort((a, b) => (a.sorting ?? 0) - (b.sorting ?? 0))

export const getCrmLeadIntegrationProviderTitle = (
  provider: string | null | undefined,
): string => {
  switch (String(provider ?? '').trim().toLowerCase()) {
    case 'vkontakte':
    case 'vk':
      return 'VK'
    case 'telegram':
      return 'Telegram'
    case 'max':
      return 'MAX'
    case 'wazzup':
      return 'Wazzup'
    default: {
      const raw = String(provider ?? '').trim()
      return raw || 'Интеграция'
    }
  }
}

export const getCrmLeadIntegrationFieldLabel = (field: Field): string => {
  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return field.name

  const integrationName =
    String(binding.integration_name ?? '').trim() ||
    `Интеграция #${binding.integration_id}`

  if (binding.provider === 'wazzup') {
    const channelName = String(binding.channel_name ?? '').trim()
    const typeLabel = getWazzupChannelTypeLabel(
      String(binding.channel_type ?? ''),
    )
    const typeMark =
      typeLabel && typeLabel !== 'неизвестный'
        ? `Wazzup ${typeLabel}`
        : 'Wazzup'

    if (channelName) {
      return `${typeMark} · ${channelName}`
    }
    return `${typeMark} · ${integrationName}`
  }

  return `${getCrmLeadIntegrationProviderTitle(binding.provider)} · ${integrationName}`
}

const getWazzupHint = (channelType: string | null | undefined): string => {
  const key = String(channelType ?? '')
    .trim()
    .toLowerCase()
  if (!key) {
    return 'Укажите ссылку или идентификатор в формате канала Wazzup'
  }
  return (
    WAZZUP_HINTS[key] ??
    'Укажите ссылку или идентификатор в формате выбранного канала Wazzup'
  )
}

const getWazzupPlaceholder = (
  channelType: string | null | undefined,
): string => {
  const key = String(channelType ?? '')
    .trim()
    .toLowerCase()
  return WAZZUP_PLACEHOLDERS[key] ?? 'Ссылка или идентификатор'
}

export const getCrmLeadIntegrationFieldHint = (field: Field): string => {
  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return ''

  if (binding.provider === 'wazzup') {
    return getWazzupHint(binding.channel_type)
  }

  const key = String(binding.provider ?? '')
    .trim()
    .toLowerCase()
  return PROVIDER_HINTS[key] ?? 'Укажите ссылку или идентификатор профиля'
}

export const getCrmLeadIntegrationFieldPlaceholder = (field: Field): string => {
  const settingsPlaceholder = (() => {
    const settings = field.settings
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
      return null
    }
    const raw = (settings as { placeholder?: unknown }).placeholder
    if (raw == null) return null
    const trimmed = String(raw).trim()
    return trimmed || null
  })()
  if (settingsPlaceholder) return settingsPlaceholder

  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return field.name

  if (binding.provider === 'wazzup') {
    return getWazzupPlaceholder(binding.channel_type)
  }

  const key = String(binding.provider ?? '')
    .trim()
    .toLowerCase()
  return PROVIDER_PLACEHOLDERS[key] ?? field.name
}

const PROVIDER_URL_PREFIXES: Record<string, string> = {
  vkontakte: 'https://vk.com/',
  vk: 'https://vk.com/',
  telegram: 'https://t.me/',
  max: 'https://max.ru/u/',
}

const WAZZUP_URL_PREFIXES: Record<string, string> = {
  telegram: 'https://t.me/',
  tgapi: 'https://t.me/',
  whatsapp: 'https://wa.me/',
  wapi: 'https://wa.me/',
  waba: 'https://wa.me/',
  vk: 'https://vk.com/',
  instagram: 'https://instagram.com/',
}

const PREFIX_TAIL_PLACEHOLDERS: Record<string, string> = {
  'https://t.me/': 'username',
  'https://vk.com/': 'id123 или username',
  'https://max.ru/u/': 'уникальный-код',
  'https://wa.me/': '79001234567',
  'https://instagram.com/': 'username',
}

const isNumericMessengerId = (value: string) => /^-?\d+$/.test(value)

const isHttpUrl = (value: string) => /^https?:\/\//i.test(value)

/** Префикс ссылки для UI (не пишется в value, пока нет хвоста). */
export const getCrmLeadIntegrationFieldUrlPrefix = (
  field: Field,
): string | null => {
  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return null

  if (binding.provider === 'wazzup') {
    const channelKey = String(binding.channel_type ?? '')
      .trim()
      .toLowerCase()
    return WAZZUP_URL_PREFIXES[channelKey] ?? null
  }

  const key = String(binding.provider ?? '')
    .trim()
    .toLowerCase()
  return PROVIDER_URL_PREFIXES[key] ?? null
}

export const getCrmLeadIntegrationFieldPrefixTailPlaceholder = (
  prefix: string | null | undefined,
): string => {
  if (!prefix) return ''
  return PREFIX_TAIL_PLACEHOLDERS[prefix] ?? ''
}

export type CrmLeadIntegrationLinkInputParts = {
  /** Показывать серый префикс слева от инпута. */
  showPrefix: boolean
  /** Значение только в инпуте (без префикса). */
  inputValue: string
}

/** Разбор сохранённого value → префикс + хвост для инпута. */
export const splitCrmLeadIntegrationLinkForInput = (
  value: string | null | undefined,
  prefix: string | null,
): CrmLeadIntegrationLinkInputParts => {
  const trimmed = String(value ?? '').trim()

  if (!prefix) {
    return { showPrefix: false, inputValue: String(value ?? '') }
  }

  if (!trimmed || trimmed.toLowerCase() === prefix.toLowerCase()) {
    return { showPrefix: true, inputValue: '' }
  }

  if (isNumericMessengerId(trimmed)) {
    return { showPrefix: true, inputValue: trimmed }
  }

  if (
    isHttpUrl(trimmed) &&
    !trimmed.toLowerCase().startsWith(prefix.toLowerCase())
  ) {
    return { showPrefix: false, inputValue: trimmed }
  }

  if (trimmed.toLowerCase().startsWith(prefix.toLowerCase())) {
    return {
      showPrefix: true,
      inputValue: trimmed.slice(prefix.length),
    }
  }

  return { showPrefix: true, inputValue: trimmed }
}

/**
 * Полная ссылка для копирования: к хвосту/числовому id добавляем префикс.
 */
export const resolveCrmLeadIntegrationLinkForCopy = (
  value: string | null | undefined,
  prefix: string | null,
): string => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''
  if (!prefix) return trimmed
  if (trimmed.toLowerCase() === prefix.toLowerCase()) return ''
  if (trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return trimmed
  if (isHttpUrl(trimmed)) return trimmed

  const tail = trimmed.replace(/^\//, '')
  if (!tail) return ''
  return `${prefix}${tail}`
}

/**
 * Сборка value из инпута.
 * Пустой хвост / голый префикс → '' (на бэк ничего «форматного» не уходит).
 */
export const composeCrmLeadIntegrationLinkFromInput = (
  inputValue: string,
  prefix: string | null,
): string => {
  const trimmed = String(inputValue ?? '').trim()
  if (!trimmed) return ''

  if (!prefix) return trimmed

  if (isHttpUrl(trimmed)) {
    if (trimmed.toLowerCase() === prefix.toLowerCase()) return ''
    return trimmed
  }

  if (isNumericMessengerId(trimmed)) {
    return trimmed
  }

  const tail = trimmed.replace(/^\//, '')
  if (!tail) return ''

  return `${prefix}${tail}`
}

/** Страховка перед Save: голый префикс → пустая строка. */
export const normalizeCrmLeadIntegrationLinkValue = (
  value: string | null | undefined,
  prefix: string | null,
): string => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''
  if (prefix && trimmed.toLowerCase() === prefix.toLowerCase()) return ''
  return trimmed
}

export const getCrmLeadIntegrationFieldMultiBindingWarning = (
  field: Field,
): string | null => {
  const bindings = getCrmLeadFieldIntegrationBindings(field)
  if (bindings.length <= 1) return null

  const names = bindings
    .map((binding) => {
      const integrationName = String(binding.integration_name ?? '').trim()
      const providerTitle = getCrmLeadIntegrationProviderTitle(binding.provider)
      return integrationName
        ? `${providerTitle}: ${integrationName}`
        : providerTitle
    })
    .filter(Boolean)

  return `Поле привязано к нескольким интеграциям (${names.join(
    ', ',
  )}). Сохранение может завершиться ошибкой — оставьте одну привязку в настройках.`
}

/** Ключ провайдера для иконки (vkontakte / telegram / max / wazzup). */
export const getCrmLeadIntegrationFieldProviderKey = (
  field: Field,
): string | null => {
  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return null
  const key = String(binding.provider ?? '')
    .trim()
    .toLowerCase()
  if (key === 'vk') return 'vkontakte'
  return key || null
}

/** Нативное Telegram или канал Wazzup telegram (не tgapi — там чат по username работает). */
export const isCrmLeadIntegrationTelegramChatField = (
  field: Field | null | undefined,
): boolean => {
  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return false

  const provider = String(binding.provider ?? '')
    .trim()
    .toLowerCase()

  if (provider === 'telegram') return true

  if (provider === 'wazzup') {
    const channelType = String(binding.channel_type ?? '')
      .trim()
      .toLowerCase()
    return channelType === 'telegram'
  }

  return false
}

export type CrmLeadIntegrationFieldChatTarget = {
  provider: CrmChatProviderKey
  conversationId: number
  unreadCount: number
}

const toPositiveId = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? Math.floor(num) : null
}

/**
 * Чат лида под конкретный binding поля (integration_id + channel для Wazzup).
 * Нет совпадения → null (кнопка disabled).
 */
export const findLeadChatForIntegrationField = (
  lead: unknown,
  field: Field,
): CrmLeadIntegrationFieldChatTarget | null => {
  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  if (!binding) return null

  const provider = parseCrmChatProviderKey(
    getCrmLeadIntegrationFieldProviderKey(field),
  )
  if (!provider) return null

  const integrationId = toPositiveId(binding.integration_id)
  if (integrationId == null) return null

  const channelId = toPositiveId(binding.channel_id)
  const externalChannelId = String(binding.external_channel_id ?? '').trim()

  const meta = getLeadProviderChatMeta(lead, provider)
  const account = getLeadProviderChatAccounts(meta).find(
    (item) => Number(item.integration_id) === integrationId,
  )

  if (account?.channels?.length) {
    const channel = account.channels.find((item) => {
      if (channelId != null && toPositiveId(item.channel_id) === channelId) {
        return true
      }
      if (
        externalChannelId &&
        String(item.external_channel_id ?? '').trim() === externalChannelId
      ) {
        return true
      }
      return false
    })

    const dialog = channel?.dialogs?.find(
      (item) => toPositiveId(item.conversation_id) != null,
    )
    const conversationId = toPositiveId(dialog?.conversation_id)
    if (conversationId != null) {
      return {
        provider,
        conversationId,
        unreadCount: Number(dialog?.unread_count) || 0,
      }
    }
  }

  const tabs = getLeadProviderChatTabs(lead, provider).filter(
    (tab) => Number(tab.integration_id) === integrationId,
  )

  const matchedTab =
    (externalChannelId
      ? tabs.find(
          (tab) =>
            String(tab.external_channel_id ?? '').trim() === externalChannelId,
        )
      : undefined) ?? (channelId == null ? tabs[0] : undefined)

  const conversationId = toPositiveId(matchedTab?.conversation_id)
  if (conversationId == null) {
    return null
  }

  return {
    provider,
    conversationId,
    unreadCount: Number(matchedTab?.unread_count) || 0,
  }
}
